import React from 'react';
import { 
  Card, 
  Box, 
  Typography, 
  Chip, 
  Divider, 
  LinearProgress, 
  alpha, 
  useTheme,
  Stack,
  Grid,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Description as DocsIcon,
  Search as SearchIcon,
  Code as CodeIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';
import { format, fromUnixTime } from 'date-fns';

/**
 * Component to display OpenSearch node metrics
 * 
 * @param {Object} props
 * @param {Object} props.node - Node data
 * @param {function} props.onViewDetails - Function to handle view details click
 */
const OpenSearchNodeCard = ({ node, onViewDetails }) => {
  const theme = useTheme();
  
  if (!node) return null;
  
  // Helper functions for color coding
  const getHeapUsageColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 85) return 'error';
    if (percent > 70) return 'warning';
    return 'success';
  };
  
  const getCpuUsageColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 80) return 'error';
    if (percent > 60) return 'warning';
    return 'success';
  };
  
  const getMemoryUsageColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 85) return 'error';
    if (percent > 70) return 'warning';
    return 'success';
  };
  
  const getDiskUsageColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 90) return 'error';
    if (percent > 75) return 'warning';
    return 'success';
  };
  
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };
  
  // Format node role for display
  const getNodeRoleBadge = (role) => {
    let color = 'default';
    
    switch (role) {
      case 'master':
        color = 'primary';
        break;
      case 'data':
        color = 'success';
        break;
      case 'ingest':
        color = 'info';
        break;
      case 'coordinating':
        color = 'secondary';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={role || 'unknown'} 
        size="small" 
        color={color}
        sx={{ height: 22, fontSize: '0.75rem' }}
      />
    );
  };
  
  return (
    <Card
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
        backdropFilter: 'blur(20px)',
        boxShadow: `0 10px 15px -3px ${alpha(theme.palette.background.default, 0.1)}, 0 4px 6px -2px ${alpha(theme.palette.background.default, 0.05)}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 25px -5px ${alpha(theme.palette.background.default, 0.1)}, 0 10px 10px -5px ${alpha(theme.palette.background.default, 0.04)}`,
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        pb: 1
      }}>
        <Box>
          <Typography variant="h6" component="div" fontWeight="bold">
            {node.hostname || node.node_name || 'Unknown Node'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            {node.node_role && getNodeRoleBadge(node.node_role)}
            {node.region && (
              <Typography variant="body2" color="text.secondary">
                {node.region}
              </Typography>
            )}
            {node.node_info?.version && (
              <Chip 
                label={`v${node.node_info.version}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
        <IconButton size="small">
          <MoreIcon fontSize="small" />
        </IconButton>
      </Box>
      
      <Box sx={{ pt: 0, pb: 1, px: 2, flexGrow: 1 }}>
        <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
          Last updated: {node._received_at ? node._received_at.substring(0, 19).replace('T', ' ') : 
                       (node.timestamp ? format(fromUnixTime(node.timestamp), 'yyyy-MM-dd HH:mm:ss') : 'Unknown')}
        </Typography>
        
        <Divider sx={{ my: 1.5 }} />
        
        <Stack spacing={2}>
          {/* JVM Heap Usage */}
          {node.node_info?.jvm && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Box display="flex" alignItems="center">
                  <MemoryIcon sx={{ color: getHeapUsageColor(node.node_info.jvm.heap_used_percent), fontSize: 18, mr: 0.75 }} />
                  <Typography variant="body2" fontWeight="medium">JVM Heap</Typography>
                </Box>
                <Typography variant="body2" fontWeight="bold" 
                  color={getHeapUsageColor(node.node_info.jvm.heap_used_percent)}
                >
                  {node.node_info.jvm.heap_used_percent || 0}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={node.node_info.jvm.heap_used_percent || 0} 
                color={getHeapUsageColor(node.node_info.jvm.heap_used_percent)}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {node.node_info.jvm.heap_used_gb?.toFixed(2) || 0} GB / {node.node_info.jvm.heap_max_gb || 0} GB
              </Typography>
            </Box>
          )}
          
          {/* System Metrics */}
          <Grid container spacing={1}>
            {/* CPU Usage */}
            <Grid item xs={6}>
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <SpeedIcon sx={{ mr: 0.75, fontSize: 18, color: getCpuUsageColor(node.system?.cpu?.load_1min || node.node_info?.process?.cpu_percent) }} />
                  CPU
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={node.system?.cpu?.load_1min || node.node_info?.process?.cpu_percent || 0} 
                    color={getCpuUsageColor(node.system?.cpu?.load_1min || node.node_info?.process?.cpu_percent)}
                    sx={{ height: 6, borderRadius: 3, flexGrow: 1, mr: 1 }}
                  />
                  <Typography variant="caption" fontWeight="bold" color={getCpuUsageColor(node.system?.cpu?.load_1min || node.node_info?.process?.cpu_percent)}>
                    {node.system?.cpu?.load_1min?.toFixed(1) || node.node_info?.process?.cpu_percent || 0}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            {/* Memory Usage */}
            <Grid item xs={6}>
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <MemoryIcon sx={{ mr: 0.75, fontSize: 18, color: getMemoryUsageColor(node.system?.memory?.percent) }} />
                  Memory
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={node.system?.memory?.percent || 0} 
                    color={getMemoryUsageColor(node.system?.memory?.percent)}
                    sx={{ height: 6, borderRadius: 3, flexGrow: 1, mr: 1 }}
                  />
                  <Typography variant="caption" fontWeight="bold" color={getMemoryUsageColor(node.system?.memory?.percent)}>
                    {node.system?.memory?.percent?.toFixed(1) || 0}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            {/* Disk Usage */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <StorageIcon sx={{ mr: 0.75, fontSize: 18, color: getDiskUsageColor(node.system?.disk?.percent) }} />
                  Disk
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={node.system?.disk?.percent || node.system?.disk?.boot_volume?.percent || 0} 
                    color={getDiskUsageColor(node.system?.disk?.percent || node.system?.disk?.boot_volume?.percent)}
                    sx={{ height: 6, borderRadius: 3, flexGrow: 1, mr: 1 }}
                  />
                  <Typography variant="caption" fontWeight="bold" color={getDiskUsageColor(node.system?.disk?.percent || node.system?.disk?.boot_volume?.percent)}>
                    {node.system?.disk?.percent?.toFixed(1) || node.system?.disk?.boot_volume?.percent?.toFixed(1) || 0}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          
          {/* Index Stats */}
          {node.node_info?.indices && (
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.background.default, 0.5),
            }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>Index Stats</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Tooltip title="Document count">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DocsIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" mr={0.5}>
                        Docs:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {node.node_info.indices.docs_count?.toLocaleString() || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Store size">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <StorageIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" mr={0.5}>
                        Size:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {node.node_info.indices.store_size_gb?.toFixed(2) || 0} GB
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Indexing operations">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CodeIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" mr={0.5}>
                        Index:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {node.node_info.indices.indexing_total?.toLocaleString() || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Search queries">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" mr={0.5}>
                        Search:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {node.node_info.indices.search_query_total?.toLocaleString() || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* File Descriptors */}
          {node.node_info?.process?.open_file_descriptors && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                File Descriptors
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(node.node_info.process.open_file_descriptors / node.node_info.process.max_file_descriptors) * 100 || 0} 
                  color="primary"
                  sx={{ height: 6, borderRadius: 3, flexGrow: 1, mr: 1 }}
                />
                <Typography variant="caption" fontWeight="bold">
                  {node.node_info.process.open_file_descriptors} / {node.node_info.process.max_file_descriptors}
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

export default OpenSearchNodeCard; 